import { sdk as imageKitSdk, } from '@wix/image-kit';
import { resizeThumbnail as _resizeThumbnail } from '@wix/communities-blog-universal/dist/src/utils/media/thumbnail';
import { isSSR as _isSSR } from './is-ssr';
const WIDTHS_MOBILE = [320, 375, 425, 768];
export const screenWidth = _isSSR() ? 980 : window.screen.width;
export const screenPixelRatio = _isSSR() ? 1 : window.devicePixelRatio;
export const getFormat = (isSSR) => !isSSR && window.__IS_WEBP_SUPPORTED__ ? 'webp' : 'png';
const snapToClosestScreenWidth = (w) => WIDTHS_MOBILE.reduce((closest, v) => {
    const d1 = v - w;
    const d2 = closest - w;
    return Math.abs(d1) < Math.abs(d2)
        ? d1 >= 0 || d2 < 0
            ? v
            : closest
        : closest;
});
const getProportionalHeight = (originalWidth, originalHeight, w) => w / (originalWidth / originalHeight);
const getProportionalWidth = (originalWidth, originalHeight, h) => h * (originalWidth / originalHeight);
const adjustSizeByPixelRatio = (originalWidth, originalHeight, w, h, pixelRatio) => {
    const nW = Math.ceil(w * pixelRatio);
    const nH = Math.ceil(h * pixelRatio);
    const isLargerThanOrig = nW > originalWidth || nH > originalHeight;
    return [
        isLargerThanOrig ? originalWidth : nW,
        isLargerThanOrig ? originalHeight : nH,
    ];
};
/**
 * Returns filled `[width, height]` of an image that is best for current device based on given params.
 */
export const getFillSize = (
/** original image width */
oW, 
/** original image height */
oH, 
/** new image width (the size that image will be shown) */
w, 
/** new image height (the size that image will be shown) */
h, isMobile, 
/** screenWidth, injected for testing, on live taken from window */
sW = screenWidth, 
/** pixelRatio, injected for testing, on live taken from window */
pR = screenPixelRatio) => {
    if (isMobile && (!w || w > sW)) {
        w = snapToClosestScreenWidth(sW);
    }
    if (w >= oW || h >= oH || !(w || h)) {
        return [oW, oH];
    }
    else if (w && h) {
        const propW = getProportionalWidth(oW, oH, h);
        const propH = getProportionalHeight(oW, oH, w);
        const scaleByW = propH >= h;
        return adjustSizeByPixelRatio(oW, oH, scaleByW ? w : propW, scaleByW ? propH : h, pR);
    }
    else if (w) {
        return adjustSizeByPixelRatio(oW, oH, w, getProportionalHeight(oW, oH, w), pR);
    }
    else if (h) {
        return adjustSizeByPixelRatio(oW, oH, getProportionalWidth(oW, oH, h), h, pR);
    }
    return undefined;
};
export const getImageUri = (image) => {
    if (typeof image === 'string') {
        return image;
    }
    if (image) {
        if ('id' in image && image.id !== '') {
            return image.id ?? ('file_name' in image ? image.file_name : undefined);
        }
        if ('file_name' in image) {
            return image.file_name;
        }
    }
    return undefined;
};
export const getImageUrl = ({ image, maxWidth = 1000, maxHeight = 1000, quality, type = 'fit', format, blur, encode = false, }) => {
    if (!image) {
        return '';
    }
    const imageUri = getImageUri(image) ?? '';
    if (!imageUri) {
        return 'url' in image ? image.url ?? '' : '';
    }
    const imageSdkScaleFn = {
        fill: imageKitSdk.getScaleToFillImageURL,
        fit: imageKitSdk.getScaleToFitImageURL,
    }[type];
    const scaledImageUrl = imageSdkScaleFn(imageUri, image.width, image.height, maxWidth, maxHeight, {
        quality,
        preferredExtension: format,
        filters: { blur },
        autoEncode: encode,
    });
    return scaledImageUrl;
};
export const resizeThumbnail = ({ pathname, width, height, imageHost, }) => _resizeThumbnail(imageHost)(pathname, width, height);
const thumbnailOptionDefaults = {
    maxWidth: 150,
    maxHeight: 150,
};
export const getThumbnailUrlFromCoverMedia = (coverMedia, options = thumbnailOptionDefaults) => {
    const image = coverMedia?.enabled
        ? coverMedia.image ?? coverMedia.video?.thumbnail ?? {}
        : {};
    return getImageUrl({
        image,
        maxWidth: options.maxWidth,
        maxHeight: options.maxHeight,
        quality: 100,
        format: 'webp',
    });
};
export const getThumbnailUrlFromMedia = (media, options = thumbnailOptionDefaults) => {
    let image;
    if (media?.displayed) {
        image =
            media.wixMedia?.image ?? media.embedMedia?.thumbnail;
    }
    return getImageUrl({
        image,
        maxWidth: options.maxWidth,
        maxHeight: options.maxHeight,
        quality: 100,
        format: 'webp',
    });
};
export const preloadImage = (imageParams) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = getImageUrl(imageParams);
        if (img.complete) {
            resolve();
        }
        img.onload = () => resolve();
        img.onerror = () => reject();
    });
};
//# sourceMappingURL=image-utils.js.map